import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import IpAcceptanceLogPanel from '../components/IpAcceptanceLogPanel';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Shield, Loader2, Copy, Eye, KeyRound, 
  Ban, CheckCircle, Download, BarChart3, FileText, Receipt,
  Clock, AlertCircle, Building2, Mail, Calendar, Trash2, Pencil, LogIn, LogOut
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPage() {
  const navigate = useNavigate();
  const { switchBusiness } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [clients, setClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [joiningBizId, setJoiningBizId] = useState(null);
  
  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  
  // Password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetClient, setResetClient] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(true);

  // Delete client dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit business name dialog
  const [editBizDialogOpen, setEditBizDialogOpen] = useState(false);
  const [editBizTarget, setEditBizTarget] = useState(null); // { id, name, client_email }
  const [editBizName, setEditBizName] = useState('');
  const [savingBizName, setSavingBizName] = useState(false);

  // Sessions/activity summary (refreshes every 60s for the "Last Activity" column)
  const [sessions, setSessions] = useState({}); // { user_id -> { is_online, last_login_at, total_seconds_today, ... } }

  useEffect(() => {
    fetchData();
  }, []);

  // Poll the sessions summary so the "online" indicator stays fresh
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/clients/sessions-summary`);
        if (cancelled) return;
        const map = {};
        for (const u of r.data?.users || []) {
          map[u.user_id] = u;
        }
        setSessions(map);
      } catch (_e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, clientsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/clients`),
        axios.get(`${API}/admin/invites`)
      ]);
      setAnalytics(analyticsRes.data);
      // Sort clients by created_at descending (newest first)
      const sortedClients = clientsRes.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setClients(sortedClients);
      // Sort invites by created_at descending (newest first)
      const sortedInvites = invitesRes.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInvites(sortedInvites);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInviteClient = async () => {
    if (!inviteEmail || !inviteCompany) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/admin/invite-client`, {
        email: inviteEmail,
        company_name: inviteCompany
      });
      setLastInviteLink(window.location.origin + response.data.invite_link);
      toast.success('Invitation created!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetClient) return;
    if (useCustomPassword && customPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      const payload = useCustomPassword && customPassword ? { custom_password: customPassword } : {};
      const response = await axios.post(`${API}/admin/clients/${resetClient.id}/reset-password`, payload);
      setTempPassword(response.data.temporary_password);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleToggleStatus = async (clientId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/status?is_active=${!currentStatus}`);
      toast.success(currentStatus ? 'Client disabled' : 'Client enabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update client status');
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteClient) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/clients/${deleteClient.id}`);
      toast.success(`Client ${deleteClient.email} deleted`);
      setDeleteDialogOpen(false);
      setDeleteClient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  const openEditBusiness = (client) => {
    if (!client?.business_id) {
      toast.error('This client has no business yet');
      return;
    }
    setEditBizTarget({ id: client.business_id, name: client.business_name, client_email: client.email });
    setEditBizName(client.business_name || '');
    setEditBizDialogOpen(true);
  };

  const handleSaveBusinessName = async () => {
    if (!editBizTarget) return;
    const trimmed = (editBizName || '').trim();
    if (!trimmed) {
      toast.error('Business name cannot be empty');
      return;
    }
    if (trimmed === editBizTarget.name) {
      setEditBizDialogOpen(false);
      return;
    }
    setSavingBizName(true);
    try {
      await axios.put(`${API}/admin/businesses/${editBizTarget.id}`, { name: trimmed });
      toast.success('Business renamed');
      setEditBizDialogOpen(false);
      setEditBizTarget(null);
      setEditBizName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename business');
    } finally {
      setSavingBizName(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm('Delete this invitation?')) return;
    
    try {
      await axios.delete(`${API}/admin/invites/${inviteId}`);
      toast.success('Invitation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invitation');
    }
  };

  const handleJoinBusiness = async (client) => {
    if (!client?.business_id) {
      toast.error('This client has no business yet');
      return;
    }
    setJoiningBizId(client.business_id);
    try {
      await axios.post(`${API}/admin/businesses/${client.business_id}/join`);
      toast.success(`Joined ${client.business_name} as admin — switching now…`);
      // switchBusiness activates the business AND reloads the page so the
      // sidebar, dashboard, and every panel re-fetches against the new context.
      await switchBusiness(client.business_id);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join business');
      setJoiningBizId(null);
    }
  };

  const handleLeaveBusiness = async (client) => {
    if (!client?.business_id) return;
    if (!window.confirm(`Leave ${client.business_name}? You will lose edit access until you rejoin.`)) return;
    setJoiningBizId(client.business_id);
    try {
      await axios.delete(`${API}/admin/businesses/${client.business_id}/leave`);
      toast.success(`Left ${client.business_name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave business');
    } finally {
      setJoiningBizId(null);
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client_data_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Shield className="w-6 h-6 text-primary" />
              Admin Panel
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage clients, invitations, and view analytics
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExportAll} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Export All Data
            </Button>
            <Button onClick={() => setInviteDialogOpen(true)} className="gap-2">
              <UserPlus className="w-4 h-4" />
              Invite Client
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-slate-500">Total Clients</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.total_clients || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-slate-500">Active</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.active_clients || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-slate-500">Pending Invites</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.pending_invites || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-slate-500">Total Invoices</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.total_invoices || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-orange-600" />
                <span className="text-xs text-slate-500">Total Bills</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.total_bills || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-teal-600" />
                <span className="text-xs text-slate-500">Logins (7d)</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.logins_last_7_days || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-indigo-600" />
                <span className="text-xs text-slate-500">Logins (30d)</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.logins_last_30_days || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="clients" className="w-full">
          <TabsList>
            <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
            <TabsTrigger value="invites">Pending Invites ({invites.filter(i => i.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="ip-log" data-testid="admin-tab-ip-log">IP Acceptance Log</TabsTrigger>
          </TabsList>
          
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <CardTitle>Client Accounts</CardTitle>
                <CardDescription>All registered clients and their businesses</CardDescription>
              </CardHeader>
              <CardContent>
                {clients.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No clients yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Business</TableHead>
                        <TableHead>Invoices</TableHead>
                        <TableHead>Bills</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead title="Last login + time spent in app this month. Refreshes every minute.">
                          Last Activity
                        </TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{client.name}</p>
                              <p className="text-xs text-slate-500">{client.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 group">
                              <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                              <span className="truncate" title={client.business_name}>{client.business_name}</span>
                              {client.business_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => openEditBusiness(client)}
                                  title="Rename business"
                                  data-testid={`edit-business-name-${client.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{client.invoice_count}</TableCell>
                          <TableCell>{client.bill_count}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <LastActivityCell session={sessions[client.id]} />
                          </TableCell>
                          <TableCell>
                            {client.is_active !== false ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                                <CheckCircle className="w-3 h-3" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                <Ban className="w-3 h-3" /> Disabled
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/admin/client/${client.id}`)}
                                title="View Data"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {client.business_id && (
                                client.admin_is_member ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleLeaveBusiness(client)}
                                    disabled={joiningBizId === client.business_id}
                                    title="Leave business (remove your admin access)"
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    data-testid={`leave-business-${client.id}`}
                                  >
                                    {joiningBizId === client.business_id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <LogOut className="w-4 h-4" />}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleJoinBusiness(client)}
                                    disabled={joiningBizId === client.business_id}
                                    title="Join as team member with full admin access"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    data-testid={`join-business-${client.id}`}
                                  >
                                    {joiningBizId === client.business_id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <LogIn className="w-4 h-4" />}
                                  </Button>
                                )
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setResetClient(client); setResetDialogOpen(true); setTempPassword(null); }}
                                title="Reset Password"
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleToggleStatus(client.id, client.is_active !== false)}
                                title={client.is_active !== false ? 'Disable' : 'Enable'}
                                className={client.is_active !== false ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                              >
                                {client.is_active !== false ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setDeleteClient(client); setDeleteDialogOpen(true); }}
                                title="Delete Client"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="invites">
            <Card>
              <CardHeader>
                <CardTitle>Client Invitations</CardTitle>
                <CardDescription>Pending and accepted invitations</CardDescription>
              </CardHeader>
              <CardContent>
                {invites.filter(i => i.status === 'pending').length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pending invitations</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Invited</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.filter(i => i.status === 'pending').map((invite) => (
                        <TableRow key={invite.id}>
                          <TableCell className="font-medium">{invite.email}</TableCell>
                          <TableCell>{invite.company_name}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-amber-600 text-xs bg-amber-50 px-2 py-1 rounded">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(invite.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(window.location.origin + `/client-signup/${invite.invite_token}`)}
                                title="Copy Link"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteInvite(invite.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Delete"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ip-log">
            <IpAcceptanceLogPanel />
          </TabsContent>
        </Tabs>

        {/* Invite Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Invite New Client
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  placeholder="client@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={lastInviteLink}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  type="text"
                  placeholder="ABC Pty Ltd"
                  value={inviteCompany}
                  onChange={(e) => setInviteCompany(e.target.value)}
                  disabled={lastInviteLink}
                />
              </div>

              {lastInviteLink && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-2 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Copy & Share This Link</span>
                  </div>
                  <p className="text-xs text-amber-600">
                    Send this link to your client. They will use it to create their account.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input value={lastInviteLink} readOnly className="text-xs" />
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(lastInviteLink)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setInviteDialogOpen(false); setLastInviteLink(null); setInviteEmail(''); setInviteCompany(''); }}>
                {lastInviteLink ? 'Done' : 'Cancel'}
              </Button>
              {!lastInviteLink && (
                <Button onClick={handleInviteClient} disabled={inviting} className="gap-2">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Create Invitation
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) { setTempPassword(null); setCustomPassword(''); setUseCustomPassword(true); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!tempPassword ? (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Reset password for <strong>{resetClient?.email}</strong>?
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={useCustomPassword}
                          onChange={() => setUseCustomPassword(true)}
                          className="accent-primary"
                          data-testid="custom-password-radio"
                        />
                        <span className="text-sm">Set custom password</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!useCustomPassword}
                          onChange={() => setUseCustomPassword(false)}
                          className="accent-primary"
                          data-testid="random-password-radio"
                        />
                        <span className="text-sm">Generate random password</span>
                      </label>
                    </div>
                    
                    {useCustomPassword && (
                      <div className="space-y-2">
                        <Label htmlFor="custom-pwd">New Password</Label>
                        <Input
                          id="custom-pwd"
                          type="text"
                          placeholder="Min 6 characters"
                          value={customPassword}
                          onChange={(e) => setCustomPassword(e.target.value)}
                          data-testid="custom-password-input"
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-2 border border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Password Reset Successful</span>
                  </div>
                  <p className="text-xs text-green-600">
                    Share this password with the client:
                  </p>
                  <div className="flex items-center gap-2">
                    <Input value={tempPassword} readOnly className="font-mono" data-testid="new-password-display" />
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(tempPassword)} data-testid="copy-new-password-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetDialogOpen(false); setTempPassword(null); setCustomPassword(''); setUseCustomPassword(true); }}>
                {tempPassword ? 'Done' : 'Cancel'}
              </Button>
              {!tempPassword && (
                <Button onClick={handleResetPassword} disabled={resetting || (useCustomPassword && customPassword.length < 6)} variant="destructive" className="gap-2" data-testid="confirm-reset-password-btn">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Reset Password
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Client Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Client
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  Are you sure you want to delete <strong>{deleteClient?.email}</strong>?
                </p>
                <p className="text-xs text-red-600">
                  This will permanently remove:
                </p>
                <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                  <li>Their user account</li>
                  <li>All businesses</li>
                  <li>All invoices, bills, customers, suppliers</li>
                  <li>All other associated data</li>
                </ul>
                <p className="text-xs text-red-800 font-bold mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteClient(null); }}>
                Cancel
              </Button>
              <Button onClick={handleDeleteClient} disabled={deleting} variant="destructive" className="gap-2">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Edit Business Name Dialog */}
        <Dialog open={editBizDialogOpen} onOpenChange={(open) => { setEditBizDialogOpen(open); if (!open) { setEditBizTarget(null); setEditBizName(''); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Rename Business
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Editing business for <strong>{editBizTarget?.client_email}</strong>.
                The new name will appear everywhere — sidebar, dashboards, exports and team views.
              </p>
              <div className="space-y-2">
                <Label htmlFor="biz-name-input">Business Name</Label>
                <Input
                  id="biz-name-input"
                  type="text"
                  value={editBizName}
                  onChange={(e) => setEditBizName(e.target.value)}
                  placeholder="e.g. ABC Pty Ltd"
                  data-testid="edit-business-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBusinessName(); }}
                  autoFocus
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditBizDialogOpen(false)} disabled={savingBizName}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveBusinessName}
                disabled={savingBizName || !editBizName.trim()}
                className="gap-2"
                data-testid="confirm-rename-business-btn"
              >
                {savingBizName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Tiny presentational helper for the Last Activity column
function LastActivityCell({ session }) {
  if (!session || !session.last_login_at) {
    return <span className="text-xs text-slate-400">Never logged in</span>;
  }
  const fmtDuration = (s) => {
    if (!s || s < 60) return '<1m';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };
  const relTime = (iso) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (!Number.isFinite(diff) || diff < 0) return 'just now';
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 31) return `${days}d ago`;
    return d.toLocaleDateString();
  };
  return (
    <div className="text-xs leading-tight" title={`Last login: ${new Date(session.last_login_at).toLocaleString()}\nThis month: ${session.session_count_this_month} session(s)`}>
      <div className="flex items-center gap-1.5">
        {session.is_online ? (
          <>
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-40 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="font-semibold text-emerald-700">Online now</span>
          </>
        ) : (
          <>
            <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
            <span className="text-slate-700 font-medium">{relTime(session.last_login_at)}</span>
          </>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        {fmtDuration(session.total_seconds_today)} today · {fmtDuration(session.total_seconds_this_month)} this month
      </div>
    </div>
  );
}
